import { inject, Injectable } from '@angular/core';
import {
  WorkflowClient,
  isWorkflowErrorPayload,
} from '@bizzdesign/sdk-workflow';
import { type AiRequest } from '../models/ai-request.model';
import { catchError, from, type Observable, map } from 'rxjs';
import {
  AI_ASSISTANT_CONFIG,
  type AiAssistantConfig,
} from '../tokens/ai-assistant-config.token';
import { AiAssistantError } from '../errors/ai-assistant.error';
import { type AiAssistantResponse } from '@bizzdesign/shared/models';
import type { OrchestratorInput } from '@bizzdesign/ai-assistant/workflows';
import { mapV1ContextToV3Context } from '../utils/ai-contextv1-to-contextv3.mapper';

@Injectable({
  providedIn: 'root',
})
export class AiAssistantService {
  private readonly workflowClient = inject(WorkflowClient);
  private readonly aiAssistantConfig =
    inject<AiAssistantConfig>(AI_ASSISTANT_CONFIG);

  sendAiRequest(request: AiRequest): Observable<AiAssistantResponse> {
    const response = this.workflowClient.callAiWorkflow<
      OrchestratorInput,
      AiAssistantResponse
    >({
      endpointPath: this.aiAssistantConfig.triggerWorkflowEndpoint,
      payload: {
        request: {
          requestConfiguration: request.config.requestContent,
          requestLiveSuggestions: request.config.requestLiveSuggestions,
        },
        context: mapV1ContextToV3Context([
          ...request.context,
          ...(request.userPrompt !== undefined
            ? [
                {
                  type: 'userPrompt' as const,
                  context: request.userPrompt,
                },
              ]
            : []),
        ]),
      },
    });

    return from(response).pipe(
      map((res) => res.payload),
      map((payload) => {
        if (isWorkflowErrorPayload(payload)) {
          throw new AiAssistantError(
            'WORKFLOW_ERROR_STATUS',
            payload.message || 'Workflow returned ERROR status.',
          );
        }
        return payload;
      }),
      catchError((error: unknown) => {
        if (error instanceof AiAssistantError) {
          throw error;
        }
        throw new AiAssistantError(
          'WORKFLOW_REQUEST_FAILED',
          error instanceof Error
            ? error.message
            : 'Failed to reach the AI workflow.',
          undefined,
          error,
        );
      }),
    );
  }
}
